import React, { useState, useEffect } from 'react';
import { generate, getUserProfile, getFieldOptions } from '../services/api';
import { useLang } from '../i18n/LanguageContext';
import { useAuth } from '../auth/AuthContext';

export default function GeneratePage() {
  const { t, lang } = useLang();
  const { user, refreshUser } = useAuth();
  const [sourcePreview, setSourcePreview] = useState(null);
  const [sourceBase64, setSourceBase64] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedImageMimeType, setGeneratedImageMimeType] = useState('image/png');
  const [outfitDetails, setOutfitDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [dbOptions, setDbOptions] = useState(null);

  // Color change state (shown after generation)
  const [mainColorChange, setMainColorChange] = useState('');
  const [topColorChange, setTopColorChange] = useState('');
  const [bottomColorChange, setBottomColorChange] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);

  useEffect(() => {
    getUserProfile().then(({ data }) => setProfile(data)).catch(() => {});
    getFieldOptions().then(({ data }) => setDbOptions(data)).catch(() => {});
    refreshUser();
  }, [refreshUser]);

  const getColorOptions = () => {
    if (dbOptions && dbOptions['color']) {
      return dbOptions['color'].map(o => ({
        value: o.value,
        label: lang === 'ar' ? o.labelAr : o.labelEn,
      }));
    }
    return t.dressOptions?.color || [];
  };

  const isFree = (profile?.subscriptionStatus ?? user?.subscriptionStatus) === 'Free';
  const userCredits = profile?.credits ?? user?.credits ?? 0;
  const limitReached = isFree && userCredits < 2 && profile && profile.uploadGenerateCount >= 2;
  const noCredits = !isFree && userCredits < 2;

  const detailFields = [
    { key: 'silhouette', label: t.silhouette, icon: 'checkroom' },
    { key: 'color', label: t.color, icon: 'palette' },
    { key: 'neckline', label: t.neckline, icon: 'content_cut' },
    { key: 'sleeves', label: t.sleeves, icon: 'styler' },
    { key: 'length', label: t.length, icon: 'straighten' },
    { key: 'bodice', label: t.bodice, icon: 'dry_cleaning' },
    { key: 'waist', label: t.waist, icon: 'straighten' },
  ];

  const handleSourceUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setSourcePreview(reader.result);
      setSourceBase64(reader.result.split(',')[1]);
      setGeneratedImage(null); setOutfitDetails(null); setError('');
      setMainColorChange(''); setTopColorChange(''); setBottomColorChange('');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleGenerate = async () => {
    if (!sourceBase64) { setError(t.uploadFirst); return; }
    setLoading(true); setError(''); setGeneratedImage(null); setOutfitDetails(null);
    setMainColorChange(''); setTopColorChange(''); setBottomColorChange('');
    try {
      const { data } = await generate(sourceBase64);
      if (data.success) {
        setGeneratedImage(data.imageBase64);
        setGeneratedImageMimeType(data.imageMimeType || 'image/png');
        setOutfitDetails(data.outfitDetails);
        setProfile(prev => prev ? { ...prev, credits: data.credits, subscriptionStatus: data.subscriptionStatus, uploadGenerateCount: (prev.uploadGenerateCount || 0) + 1 } : prev);
        refreshUser();
      } else { setError(data.error || 'Generation failed.'); }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.Error || err.message);
    }
    setLoading(false);
  };

  const handleRegenerate = async () => {
    if (!sourceBase64) return;
    const isTwoPieceType = outfitDetails?.dressType === 'TwoPiece';
    const colorChanges = isTwoPieceType
      ? { topColorChange: topColorChange || null, bottomColorChange: bottomColorChange || null }
      : { mainColorChange: mainColorChange || null };

    // Don't regenerate if no color selected
    if (isTwoPieceType && !topColorChange && !bottomColorChange) return;
    if (!isTwoPieceType && !mainColorChange) return;

    setRegenLoading(true); setError('');
    try {
      const { data } = await generate(sourceBase64, colorChanges);
      if (data.success) {
        setGeneratedImage(data.imageBase64);
        setGeneratedImageMimeType(data.imageMimeType || 'image/png');
        setOutfitDetails(data.outfitDetails);
        setMainColorChange(''); setTopColorChange(''); setBottomColorChange('');
        setProfile(prev => prev ? { ...prev, credits: data.credits, subscriptionStatus: data.subscriptionStatus, uploadGenerateCount: (prev.uploadGenerateCount || 0) + 1 } : prev);
        refreshUser();
      } else { setError(data.error || 'Generation failed.'); }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.Error || err.message);
    }
    setRegenLoading(false);
  };

  const isAnyLoading = loading || regenLoading;
  const isTwoPiece = outfitDetails?.dressType === 'TwoPiece';
  const hasColorSelected = isTwoPiece ? (!!topColorChange || !!bottomColorChange) : !!mainColorChange;

  const labelClasses = "flex items-center gap-1.5 text-[11px] font-semibold text-[#64748b] uppercase tracking-wide";

  return (
    <div className="max-w-[1100px] w-full mx-auto px-6 py-8 pb-12 space-y-8">

      {/* Two-column layout: left narrow, right wide */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6 items-start mt-4">

        {/* ── Left Column: Source Image + Generate Button ── */}
        <div className="space-y-4">
          {/* Source image area */}
          <div className="bg-surface rounded-xl shadow-soft border border-border overflow-hidden group hover:border-[#FA6868]/50 transition-colors">
            <input type="file" accept="image/*" onChange={handleSourceUpload} className="hidden" id="source-upload" />
            <label
              htmlFor="source-upload"
              className="flex items-center justify-center cursor-pointer min-h-[350px] transition-all duration-200 hover:bg-gray-50/50"
            >
              {sourcePreview ? (
                <div className="relative w-full h-full p-4">
                  <img src={sourcePreview} alt="Source" className="w-full h-full object-contain max-h-[320px] rounded-lg shadow-sm" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-xl">
                    <span className="bg-white/90 text-gray-800 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-lg">
                      <span className="material-symbols-outlined text-[18px] text-[#FA6868]">edit</span>
                      Change Image
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-[#64748b] text-center p-6">
                  <span className="material-symbols-outlined text-5xl block mb-3 text-gray-300 group-hover:text-[#FA6868] transition-colors duration-200">cloud_upload</span>
                  <span className="text-sm font-semibold text-gray-800">{t.sourceImageLabel}</span>
                  <span className="text-xs mt-1.5 block max-w-[200px] mx-auto leading-relaxed">{t.uploadSourceText}</span>
                </div>
              )}
            </label>
          </div>

          {/* Credits / Limit info */}
          {profile && (
            <div className="bg-surface rounded-lg border border-border px-4 py-3 flex items-center justify-between text-sm shadow-sm">
              {isFree && userCredits < 2 ? (
                <span className="text-[#64748b] flex items-center gap-1.5">
                  <span className="font-medium text-gray-800">{profile.uploadGenerateCount}</span> {t.of} 2 {t.generationsUsed}
                </span>
              ) : (
                <div className="flex items-center gap-1.5 text-gray-700 font-medium">
                  <span className="material-symbols-outlined text-[20px]" style={{ color: '#fbbf24' }}>toll</span>
                  {userCredits} {t.creditsRemaining}
                </div>
              )}
              <span className="text-xs px-3 py-1 rounded-full font-semibold badge-cost">
                {t.costPerGeneration}
              </span>
            </div>
          )}

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={isAnyLoading || !sourceBase64 || limitReached || noCredits}
            className={`w-full bg-[#FA6868] hover:bg-[#e65b5b] text-white font-semibold py-3.5 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center gap-2 text-sm ${
              (isAnyLoading || !sourceBase64 || limitReached || noCredits) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">
              {loading ? 'hourglass_top' : (limitReached || noCredits) ? 'lock' : 'auto_awesome'}
            </span>
            {loading ? t.generating : t.generateBtn}
          </button>

          {/* ── Color Change Section (shown after successful generation) ── */}
          {outfitDetails && !loading && (
            <div className="bg-surface rounded-xl border border-border shadow-soft overflow-hidden" style={{ animation: 'fadeIn 0.4s ease' }}>
              {/* Detected Dress Type Header */}
              <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-border">
                <div className={labelClasses + " mb-1.5"}>
                  <span className="material-symbols-outlined text-[14px] text-[#FA6868]">checkroom</span>
                  {t.detectedDressType}
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#FA6868]/10 text-[#FA6868]">
                    <span className="material-symbols-outlined text-[14px]">{isTwoPiece ? 'layers' : 'checkroom'}</span>
                    {isTwoPiece ? t.twoPiece : t.onePiece}
                  </span>
                  {isTwoPiece ? (
                    <span className="text-xs text-[#64748b]">
                      {outfitDetails.topColor && <span className="font-medium">{outfitDetails.topColor}</span>}
                      {outfitDetails.topColor && outfitDetails.bottomColor && ' + '}
                      {outfitDetails.bottomColor && <span className="font-medium">{outfitDetails.bottomColor}</span>}
                    </span>
                  ) : (
                    <span className="text-xs text-[#64748b] font-medium">{outfitDetails.topColor || outfitDetails.color}</span>
                  )}
                </div>
              </div>

              {/* Color Dropdowns */}
              <div className="px-4 py-3 space-y-3">
                {isTwoPiece ? (
                  <>
                    {/* Top Color */}
                    <div>
                      <label className={labelClasses + " mb-1.5"}>
                        <span className="material-symbols-outlined text-[14px] text-[#FA6868]">palette</span>
                        {t.topColor}
                      </label>
                      <select
                        value={topColorChange}
                        onChange={(e) => setTopColorChange(e.target.value)}
                        className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#FA6868]/30 focus:border-[#FA6868] transition-all"
                      >
                        <option value="">{t.noChange}</option>
                        {getColorOptions().map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    {/* Bottom Color */}
                    <div>
                      <label className={labelClasses + " mb-1.5"}>
                        <span className="material-symbols-outlined text-[14px] text-[#FA6868]">palette</span>
                        {t.bottomColor}
                      </label>
                      <select
                        value={bottomColorChange}
                        onChange={(e) => setBottomColorChange(e.target.value)}
                        className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#FA6868]/30 focus:border-[#FA6868] transition-all"
                      >
                        <option value="">{t.noChange}</option>
                        {getColorOptions().map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  /* One Piece - Main Color */
                  <div>
                    <label className={labelClasses + " mb-1.5"}>
                      <span className="material-symbols-outlined text-[14px] text-[#FA6868]">palette</span>
                      {t.mainColor}
                    </label>
                    <select
                      value={mainColorChange}
                      onChange={(e) => setMainColorChange(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#FA6868]/30 focus:border-[#FA6868] transition-all"
                    >
                      <option value="">{t.noChange}</option>
                      {getColorOptions().map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Regenerate with Color Button */}
                <button
                  onClick={handleRegenerate}
                  disabled={regenLoading || !hasColorSelected || limitReached || noCredits}
                  className={`w-full bg-[#fbbf24] hover:bg-[#f59e0b] text-gray-900 font-semibold py-3 rounded-lg shadow-sm transition-all duration-200 flex items-center justify-center gap-2 text-sm ${
                    (regenLoading || !hasColorSelected || limitReached || noCredits) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'
                  }`}
                >
                  <span className="material-symbols-outlined text-[18px]">
                    {regenLoading ? 'hourglass_top' : 'refresh'}
                  </span>
                  {regenLoading ? t.generating : t.regenerateWithColor}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="px-4 py-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100">{error}</div>
          )}
        </div>

        {/* ── Right Column: Image Preview ── */}
        <div className="bg-surface rounded-xl shadow-soft border border-border p-6 min-h-[466px] flex flex-col">
          {/* Loading state */}
          {isAnyLoading && (
            <div className="flex-1 flex flex-col items-center justify-center" style={{ animation: 'fadeIn 0.3s ease' }}>
              <div className="w-12 h-12 border-4 border-gray-200 border-t-[#FA6868] rounded-full animate-spin"></div>
              <p className="text-[#64748b] mt-5 text-sm font-medium">{t.generatingLoader}</p>
            </div>
          )}

          {/* Generated image */}
          {!isAnyLoading && generatedImage && (
            <div className="flex-1 flex items-center justify-center" style={{ animation: 'fadeIn 0.4s ease' }}>
              <img
                src={`data:${generatedImageMimeType};base64,${generatedImage}`}
                alt="Generated"
                className="max-w-full max-h-[520px] rounded-xl shadow-lg object-contain"
              />
            </div>
          )}

          {/* Empty state */}
          {!isAnyLoading && !generatedImage && (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <span className="material-symbols-outlined text-5xl text-gray-200 block mb-3">image</span>
              <p className="text-sm text-[#64748b] leading-relaxed">Upload a source image and click Generate<br/>to see the resulting design here</p>
            </div>
          )}
        </div>
      </div>

      {/* Outfit Details — full width below */}
      {outfitDetails && !isAnyLoading && (
        <div className="bg-surface rounded-xl p-6 sm:p-7 shadow-soft border border-border" style={{ animation: 'fadeIn 0.5s ease' }}>
          <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-3">
            <span className="w-[3px] h-6 bg-[#fbbf24] rounded-full"></span>
            {t.outfitDetailsTitle}
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {detailFields.map(({ key, label, icon }) => {
              const val = outfitDetails[key];
              if (!val || val === 'None' || val === '') return null;
              
              return (
                <div key={key} className="bg-gray-50 rounded-lg px-4 py-3 border border-border">
                  <div className={labelClasses + " mb-1"}>
                    <span className="material-symbols-outlined text-[14px] text-[#FA6868]">{icon}</span>
                    {label}
                  </div>
                  <div className="text-sm text-gray-800 leading-relaxed font-medium">{val}</div>
                </div>
              );
            })}
          </div>
          
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
            <span className="text-xs font-medium text-[#64748b]">ID: #{outfitDetails.id}</span>
            <span className="text-xs font-medium text-[#64748b]">{new Date(outfitDetails.createdAt).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}
